import React from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import SkuflixThumbUpIcon from "core/icons/skuflix/SkuflixThumbUpIcon";
import SkuflixThumbDownIcon from "core/icons/skuflix/SkuflixThumbDownIcon";
import Tooltip from "@mui/material/Tooltip";
import { numberFormat } from "core/utils/string";
import { useMutation } from "@apollo/client";
import { CreateLikeMutation, DeleteLikeMutation } from "arena/mutations";
// import { ItemLikesQuery } from "arena/queries";
import { useViewer } from "core/components/ViewerProvider";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

export default function Likes({
  contentId,
  commentId,
  likes,
  borderRadius,
  compact = false,
}) {
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const isCompact = compact && !sm;

  const { t } = useTranslation("arena");
  const viewer = useViewer();

  const [createLike] = useMutation(CreateLikeMutation);

  const [deleteLike] = useMutation(DeleteLikeMutation, {
    variables: { input: { contentId, commentId } },
  });

  const deleteLikes = () => {
    deleteLike();
  };

  const onClickLike = () => {
    if (!viewer) return document.querySelector(".js-sign-in").click();

    if (likes?.liked) {
      deleteLikes();
      return;
    }

    createLike({
      variables: {
        input: {
          contentId,
          commentId,
          type: "LIKE",
        },
      },
    });
  };

  const onClickDislike = () => {
    if (!viewer) return document.querySelector(".js-sign-in").click();

    if (likes?.disliked) {
      deleteLikes();
      return;
    }

    createLike({
      variables: {
        input: {
          contentId,
          commentId,
          type: "DISLIKE",
        },
      },
    });
  };

  const count = numberFormat(likes?.count || 0);
  const buttonSize = isCompact ? "small" : sm ? "large" : "medium";

  return (
    <Stack direction="row" spacing={0}>
      <Tooltip title={likes?.liked ? t("likes.unlike") : t("likes.like")}>
        <Button
          variant="text"
          color="inherit"
          startIcon={
            <SkuflixThumbUpIcon
              sx={isCompact ? { fontSize: 17 } : undefined}
              color={likes?.liked ? "primary" : "inherit"}
            />
          }
          size={buttonSize}
          onClick={onClickLike}
          sx={{
            borderRadius,
            ...(isCompact && {
              minWidth: 0,
              px: 1,
              fontSize: "0.8125rem",
              "& .MuiButton-startIcon": {
                marginRight: 0.5,
              },
            }),
          }}
        >
          {count}
        </Button>
      </Tooltip>
      <Tooltip
        title={likes?.disliked ? t("likes.undislike") : t("likes.dislike")}
      >
        <Button
          variant="text"
          color="inherit"
          size={buttonSize}
          onClick={onClickDislike}
          sx={{
            minWidth: isCompact ? 32 : 40,
            ...(isCompact && {
              px: 0.75,
            }),
            borderRadius,
          }}
        >
          <SkuflixThumbDownIcon
            sx={isCompact ? { fontSize: 17 } : undefined}
            color={likes?.disliked ? "primary" : "inherit"}
          />
        </Button>
      </Tooltip>
    </Stack>
  );
}

Likes.propTypes = {
  likes: PropTypes.shape({
    liked: PropTypes.bool,
    disliked: PropTypes.bool,
    count: PropTypes.number,
    id: PropTypes.string,
  }),
  contentId: PropTypes.string,
  commentId: PropTypes.string,
  borderRadius: PropTypes.number,
  compact: PropTypes.bool,
};
